import React, { useState } from 'react';
import {
  Box, Typography, Paper, ToggleButtonGroup, ToggleButton, Card, CardContent,
  CardActionArea, Chip, Tooltip, Collapse, IconButton, Grid, Button, Divider,
  alpha, useTheme,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShieldIcon from '@mui/icons-material/Shield';
import ScienceIcon from '@mui/icons-material/Science';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Quick actions available in the mode switcher
const QUICK_ACTIONS = [
  { id: 'safe_mode', label: 'Safe Mode', desc: 'Lower risk, tighter SL, higher confidence', icon: 'shield', color: 'success' },
  { id: 'profit_lock', label: 'Lock Profits', desc: 'Tighter trailing, earlier activation', icon: 'lock', color: 'warning' },
  { id: 'cooldown_up', label: 'Cooldown +', desc: 'Increase cooldown to reduce overtrading', icon: 'hourglass_empty', color: 'info' },
  { id: 'buy_only', label: 'Buy Only', desc: 'Block all sell entries', icon: 'menu_book', color: 'primary' },
];

// Guided settings recommendations based on mode + experience status
const GUIDED_TIPS = {
  mode1: [
    { condition: (s) => (s?.stopLossPercent || 0) > 1.2, tip: 'SL is wide for scalp mode. Consider tightening to 1.0% or less.', severity: 'warning' },
    { condition: (s) => !s?.scalpGateEnabled, tip: 'Scalp Gate is OFF. Enable it for better Mode 1 entry filtering.', severity: 'info' },
    { condition: (s) => s?.rescueScaleInEnabled, tip: 'Rescue is ON but rarely helps in scalp mode. Consider disabling.', severity: 'warning' },
    { condition: (s) => !s?.useTrailingStop, tip: 'Trailing Stop is OFF. Scalps need fast exits — enable it.', severity: 'error' },
  ],
  mode2: [
    { condition: (s) => (s?.stopLossPercent || 0) < 1.0, tip: 'SL is tight for trend mode. Consider widening to 1.2%+ to avoid premature stops.', severity: 'info' },
    { condition: (s) => !s?.rescueScaleInEnabled, tip: 'Rescue is OFF. Trend mode benefits from rescue scale-in on pullbacks.', severity: 'info' },
    { condition: (s) => !s?.adaptiveProfitTrailEnabled, tip: 'Adaptive Profit Trail is OFF. Enable for dynamic exit in trends.', severity: 'info' },
    { condition: (s) => !s?.multiTargetTPEnabled, tip: 'Multi-Target TP is OFF. Partial exits let you lock profits while riding trends.', severity: 'info' },
  ],
  experience: [
    { condition: (s) => s?.experienceModeEnabled && !s?.experienceAutoSuggestEnabled, tip: 'Auto-Suggest is OFF. Enable it so the system generates recommendations automatically.', severity: 'info' },
    { condition: (s) => s?.experienceModeEnabled && s?.experienceMinTradesForDecision < 10, tip: 'Min trades for decision is very low. Consider 15-20 for statistically meaningful results.', severity: 'warning' },
    { condition: (s) => s?.experienceModeEnabled && !s?.experienceNoTradeWeekends, tip: 'Weekend trading is allowed. Crypto weekends have different liquidity — consider blocking.', severity: 'info' },
  ],
};

// Mode 1: Scalp profiles (leverage tiers)
const MODE1_PROFILES = [
  {
    id: 'many_small_wins_scalp_5x',
    name: 'Conservative',
    leverage: '5×',
    tradeFrequency: 'High',
    avgHold: 'Short (5-30min)',
    riskStyle: 'Tight SL 1.0%, No Rescue',
    specs: { sl: '1.0%', tp: '1.2%', rr: '1.2:1', conf: 84, rescue: false },
  },
  {
    id: 'many_small_wins_scalp_10x',
    name: 'Balanced',
    leverage: '10×',
    tradeFrequency: 'Medium-High',
    avgHold: 'Short (3-20min)',
    riskStyle: 'Tight SL 0.8%, No Rescue',
    specs: { sl: '0.8%', tp: '1.0%', rr: '1.25:1', conf: 88, rescue: false },
  },
  {
    id: 'many_small_wins_scalp_20x',
    name: 'Aggressive',
    leverage: '20×',
    tradeFrequency: 'Medium',
    avgHold: 'Very Short (2-15min)',
    riskStyle: 'Very Tight SL 0.6%, Buy-Only',
    specs: { sl: '0.6%', tp: '0.9%', rr: '1.5:1', conf: 92, rescue: false },
  },
];

// Mode 2: Trend/Hybrid/Pro profiles
const MODE2_PROFILES = [
  {
    id: 'fewer_larger_wins_hybrid_5x',
    name: 'Conservative',
    leverage: '5×',
    tradeFrequency: 'Low-Medium',
    avgHold: 'Medium (30min-2h)',
    riskStyle: 'Flexible SL 1.4%, Rescue ON (3 levels)',
    specs: { sl: '1.4%', tp: '2.2%', rr: '1.57:1', conf: 82, rescue: true },
  },
  {
    id: 'fewer_larger_wins_trend_10x',
    name: 'Balanced',
    leverage: '10×',
    tradeFrequency: 'Low',
    avgHold: 'Medium (20min-1h)',
    riskStyle: 'Flexible SL 1.0%, Rescue ON (2 levels)',
    specs: { sl: '1.0%', tp: '1.8%', rr: '1.8:1', conf: 86, rescue: true },
  },
  {
    id: 'pro_trader_brain_5x',
    name: 'Aggressive (Pro Brain)',
    leverage: '5×',
    tradeFrequency: 'Very Low',
    avgHold: 'Long (1-4h)',
    riskStyle: 'AI-managed, Thesis Memory, Rescue ON',
    specs: { sl: '1.0%', tp: '1.5%', rr: '1.5:1', conf: 82, rescue: true },
  },
];

function ProfileCard({ profile, isActive, onClick, modeColor }) {
  const theme = useTheme();
  return (
    <Card
      elevation={isActive ? 4 : 0}
      sx={{
        border: '1px solid',
        borderColor: isActive ? modeColor : 'divider',
        bgcolor: isActive ? alpha(modeColor, 0.06) : 'background.paper',
        transition: 'all 0.2s',
        '&:hover': { borderColor: modeColor, transform: 'translateY(-1px)' },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">{profile.name}</Typography>
            <Chip label={profile.leverage} size="small" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SpeedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Frequency: {profile.tradeFrequency}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Hold: {profile.avgHold}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ShieldIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">{profile.riskStyle}</Typography>
            </Box>
          </Box>

          {isActive && (
            <Chip label="Active" size="small" color="success" variant="outlined" sx={{ mt: 1, fontSize: '0.65rem', height: 20 }} />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function ModeSwitcher({ modeInfo, scheduleData, onModeChange, onQuickAction }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const currentModeType = modeInfo?.isMode1 ? 'mode1' : modeInfo?.isMode2 ? 'mode2' : 'mode1';
  const [selectedModeType, setSelectedModeType] = useState(currentModeType);

  const mode1Color = theme.palette.warning.main;
  const mode2Color = theme.palette.info.main;
  const activeColor = selectedModeType === 'mode1' ? mode1Color : mode2Color;
  const profiles = selectedModeType === 'mode1' ? MODE1_PROFILES : MODE2_PROFILES;

  const handleModeTypeChange = (_, newType) => {
    if (newType) setSelectedModeType(newType);
  };

  const handleProfileSelect = (profileId) => {
    if (onModeChange) onModeChange(profileId);
  };

  // Compute guided tips for current mode + experience
  const activeTips = [
    ...(GUIDED_TIPS[selectedModeType] || []),
    ...(GUIDED_TIPS.experience || []),
  ].filter(t => t.condition(scheduleData));

  const modeDescription = selectedModeType === 'mode1'
    ? 'Fast confirmation, tight stops, many small wins. Best in active markets.'
    : 'Fewer entries, wider targets, let winners run. Best in trending markets.';

  const whatChanges = selectedModeType === 'mode1'
    ? 'Entry: strict momentum gates, fast confidence. Exit: tight trailing, early profit lock. Risk: small SL, no rescue.'
    : 'Entry: trend-following, thesis memory. Exit: wide trailing, adaptive TP. Risk: flexible SL, rescue enabled.';

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: alpha(activeColor, 0.3),
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Mode Type Toggle */}
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={selectedModeType}
          exclusive
          onChange={handleModeTypeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              fontSize: '0.8rem',
            },
          }}
        >
          <ToggleButton value="mode1" sx={{
            '&.Mui-selected': { bgcolor: alpha(mode1Color, 0.15), color: mode1Color, borderColor: mode1Color },
          }}>
            <BoltIcon sx={{ fontSize: 16, mr: 0.5 }} /> Mode 1: Scalp
          </ToggleButton>
          <ToggleButton value="mode2" sx={{
            '&.Mui-selected': { bgcolor: alpha(mode2Color, 0.15), color: mode2Color, borderColor: mode2Color },
          }}>
            <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} /> Mode 2: Trend / Hybrid
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {modeDescription}
        </Typography>

        <Tooltip title="What this mode changes">
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded: What Changes + Profile Cards */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {/* What changes tooltip */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, p: 1, borderRadius: 1, bgcolor: alpha(activeColor, 0.04) }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, mt: 0.2, color: activeColor }} />
            <Typography variant="caption" color="text.secondary">{whatChanges}</Typography>
          </Box>

          {/* Profile Cards */}
          <Grid container spacing={1.5}>
            {profiles.map(profile => (
              <Grid item xs={12} sm={4} key={profile.id}>
                <ProfileCard
                  profile={profile}
                  isActive={modeInfo?.modeId === profile.id}
                  onClick={() => handleProfileSelect(profile.id)}
                  modeColor={activeColor}
                />
              </Grid>
            ))}
          </Grid>

          {/* Quick Actions */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TuneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary">Quick Actions</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {QUICK_ACTIONS.map(qa => (
              <Tooltip key={qa.id} title={qa.desc}>
                <Button
                  size="small"
                  variant="outlined"
                  color={qa.color}
                  onClick={() => onQuickAction?.(qa.id)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, minWidth: 0 }}
                  startIcon={<span style={{ fontSize: 14 }}>{qa.icon}</span>}
                >
                  {qa.label}
                </Button>
              </Tooltip>
            ))}
          </Box>

          {/* Guided Settings Tips */}
          {activeTips.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">Guided Settings</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {activeTips.map((t, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1, p: 0.75, borderRadius: 1,
                      bgcolor: alpha(
                        t.severity === 'error' ? theme.palette.error.main :
                        t.severity === 'warning' ? theme.palette.warning.main :
                        theme.palette.info.main, 0.06
                      ),
                    }}
                  >
                    <Box sx={{
                      width: 6, height: 6, borderRadius: '50%', mt: 0.6, flexShrink: 0,
                      bgcolor: t.severity === 'error' ? 'error.main' : t.severity === 'warning' ? 'warning.main' : 'info.main',
                    }} />
                    <Typography variant="caption" color="text.secondary">{t.tip}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Experience Mode badge */}
          {scheduleData?.experienceModeEnabled && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                <ScienceIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Experience Mode is active — settings may be adjusted automatically by the ML engine. Check the ML / Experiments tab for details.
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
